<script setup lang="ts">
import { ref, nextTick, watch } from 'vue';
import { useRoute } from 'vue-router';
import { useAllMerchantsStore } from '@apps/ops/src/store';
import { InputField } from '@ds/components';
import debounce from '@shared/utils/debounce';
import { Merchant, Division } from '@shared/api/merchants';
import { getMerchant } from '@shared/api/api';
import router from '../router';

const route = useRoute();
const allMerchantsStore = useAllMerchantsStore();

/**
 * OPEN/CLOSE DROPDOWN
 */
const searchTerm = ref();
const inputContainer = ref<HTMLDivElement>();
const showDropdown = ref();

const updateMerchants = async () => {
	const haveMerchants = Object.values(allMerchantsStore.merchantsData).length > 0;

	const cachedSearchTerm = sessionStorage.getItem('merchantSearchTerm');
	searchTerm.value = selectedMerchant.value?.displayName || cachedSearchTerm || '';

	if (!haveMerchants && showDropdown.value) {
		await allMerchantsStore.getAllMerchants(searchTerm.value);
	}
};

const handleOpenDropdown = () => {
	showDropdown.value = !showDropdown.value;
	if (showDropdown.value) updateMerchants();
	nextTick(() =>
		(inputContainer.value?.querySelector('[data-ts*="input"]') as HTMLInputElement)?.focus()
	);
};

const handleClickOutside = () => {
	showDropdown.value = false;
	if (!selectedDivision.value) selectedMerchant.value = null;
};

const handleMerchantSearch = debounce((val) => {
	sessionStorage.setItem('merchantSearchTerm', JSON.stringify(val));
	allMerchantsStore.getAllMerchants(val as string);
}, 300);

/**
 * SELECT/CLEAR MERCHANT AND DIVISION
 */
const selectedMerchant = ref();
const selectedDivision = ref();
const showDivisions = ref();

const handleClickMerchant = (merchant: Merchant) => {
	if (merchant.divisions.length === 1) {
		selectedMerchant.value = merchant;
		handleClickDivision(merchant.divisions[0]);
	} else if (showDivisions.value && merchant?.code === selectedMerchant.value?.code) {
		showDivisions.value = false;
	} else {
		selectedMerchant.value = merchant;
		showDivisions.value = true;
	}
};

const handleClickDivision = async (division: Division) => {
	showDropdown.value = false;
	sessionStorage.setItem('merchantSearchTerm', selectedMerchant.value.displayName);
	searchTerm.value = selectedMerchant.value.displayName;
	selectedDivision.value = { code: division.code, displayName: division.displayName };

	router.replace({
		query: {
			merchantCode: selectedMerchant.value.code,
			divisionCode: selectedDivision.value.code,
		},
	});
};

const clearSelectedMerchant = () => {
	selectedMerchant.value = null;
	selectedDivision.value = null;
	sessionStorage.removeItem('merchantSearchTerm');
};

const handleClearInput = () => {
	clearSelectedMerchant();
	router.replace({ query: {} });

	allMerchantsStore.getAllMerchants();
	showDropdown.value = false;
};

watch(
	() => route?.query?.merchantCode,
	async () => {
		if (!route?.query?.merchantCode) {
			clearSelectedMerchant();
			return;
		}

		const merchant = await getMerchant(route.query.merchantCode.toString());
		selectedMerchant.value = merchant;
		selectedDivision.value = merchant?.divisions?.find(
			(division: Division) => division.code === route.query.divisionCode
		);
	},
	{ immediate: true }
);
</script>
<template>
	<div class="merchant-dropdown" v-click-outside="handleClickOutside">
		<button
			type="button"
			class="merchant-dropdown__btn"
			data-test="selectMerchantButton"
			@click="handleOpenDropdown"
		>
			<span v-if="!selectedDivision?.displayName">{{
				$t('ops.selectMerchant.selectMerchantPlaceholder')
			}}</span>
			<span v-else class="text-weight-medium">{{ selectedDivision.displayName }}</span>
			<span class="bx bx-chevron-down ml-1" />
		</button>

		<div
			v-show="showDropdown"
			class="merchant-dropdown__dropdown popover-container"
			data-test="selectMerchantDropdown"
		>
			<div ref="inputContainer" class="sticky top-0 pt-2 pb-4 mb-4 bg-white">
				<p class="my-3 text text-weight-medium text-gray-800">
					{{ $t('ops.selectMerchant.searchLabel') }}
				</p>
				<InputField
					:value="searchTerm"
					:placeholder="$t('ops.selectMerchant.searchPlaceholder')"
					is-search
					alt-style
					show-clear-btn
					@clearInput="handleClearInput"
					@input="handleMerchantSearch"
				/>
			</div>

			<template v-if="allMerchantsStore.loading">
				<div
					v-for="i in 5"
					:key="i"
					class="flex items-center w-full bg-neutral-50 p-3 rounded-2 m-2 py-4 animate-bg"
				>
					<div class="bg-neutral-100 p-4 rounded-2 mr-4 text-md" />
					<div class="bx bx-chevron-down text-lg text-neutral-200 ml-auto" />
				</div>
			</template>

			<template v-else>
				<button
					v-for="(merchant, i) in (allMerchantsStore.merchantsData! as Merchant[])"
					:key="merchant.code"
					:class="[
						'merchant-dropdown__dropdown__item',
						{
							'merchant-dropdown__dropdown__item--active':
								merchant.code === selectedMerchant?.code,
						},
					]"
					:data-test="`selectMerchantDropdownOption-${i}`"
					@click.prevent="handleClickMerchant(merchant)"
				>
					<div class="flex items-center w-full">
						<div
							:class="[
								'bg-neutral-100 px-4 py-3 rounded-2 mr-4 text-md',
								{ 'bg-neutral-50': merchant.code === selectedMerchant?.code },
							]"
						>
							{{ merchant?.displayName.substring(0, 1).toUpperCase() }}
						</div>
						<div class="flex flex-col">
							<p class="text text-weight-medium">{{ merchant?.displayName }}</p>
							<p class="text-sm">{{ merchant?.code }}</p>
						</div>
						<div
							v-show="merchant?.divisions?.length > 1"
							class="bx bx-chevron-down text-lg text-neutral-400 ml-auto"
						/>
					</div>

					<div
						v-if="
							(showDivisions ||
							selectedMerchant?.divisions.some(
								(div: Division) => div.code === selectedDivision?.code
							)) &&
								selectedMerchant?.code === merchant?.code
						"
						class="merchant-dropdown__dropdown__item__divisions"
					>
						<button
							v-for="division in selectedMerchant.divisions"
							:key="division?.code"
							:class="[
								'merchant-dropdown__dropdown__item__divisions__item',
								{
									'merchant-dropdown__dropdown__item__divisions__item--active':
										selectedDivision?.code === division?.code,
								},
							]"
							@click.stop="handleClickDivision(division)"
						>
							<div class="bg-neutral-50 px-3 py-2 rounded-2 mr-4 text-neutral-500">
								{{ division?.displayName.substring(0, 1).toUpperCase() }}
							</div>
							<div class="flex flex-col">
								<p class="text text-weight-medium truncate title-max-width">
									{{ division?.displayName }}
								</p>
								<p class="text-sm">{{ division?.code }}</p>
							</div>
						</button>
					</div>
				</button>
			</template>
		</div>
	</div>
</template>

<style lang="scss" scoped>
.merchant-dropdown {
	position: relative;
	z-index: $z-index-3;

	&__btn {
		height: $height-button;
		color: $color-neutral-500;
		padding: $spacing-2;
		border: $border-width-1 $border-style $color-neutral-500;
		border-radius: $border-radius-2;
		background-color: $color-white;
		cursor: pointer;
		font-size: $font-size;

		&:disabled {
			background-color: $color-gray-50;
			color: $color-gray-200;
			border: $border-width-1 $border-style $color-gray-200;
		}

		@media (hover: hover) {
			&:hover {
				background-color: $color-neutral-100;
				border-color: transparent;
			}
		}

		&:focus-visible,
		&:focus-within {
			outline: $border-width-2 $border-style $color-focus;
		}
	}

	&__dropdown {
		min-width: min(60vw, 480px);
		max-height: min(80vh, 480px);
		position: absolute;
		top: 100%;
		right: 0;
		background-color: $color-white;
		color: black;
		width: 100%;
		height: auto;
		border-radius: $border-radius-2;
		border: $border-width-1 $border-style $color-neutral-200;
		box-shadow: $box-shadow-dropdown;
		margin-top: $spacing-1;
		padding: 0 $spacing-4;
		cursor: pointer;

		&__item {
			width: 100%;
			text-align: left;
			display: flex;
			flex-direction: column;
			padding: $spacing-2;
			margin: $spacing-3 0;
			border-radius: $border-radius-2;
			transition: background-color 300ms ease-in-out;

			overflow: hidden;

			&:focus-visible {
				outline: $border-width-2 $border-style $color-focus;
			}

			@media (hover: hover) {
				&:hover,
				&:focus-visible,
				&--active {
					background-color: $color-neutral-100;
					transition: background-color 300ms ease-in-out;
				}
			}

			&__divisions {
				width: 100%;
				margin-top: $spacing-2;

				&__item {
					width: 100%;
					text-align: left;
					padding: $spacing-2 $spacing-4;
					margin: $spacing-3 0;
					border-radius: $border-radius-2;
					display: flex;
					align-items: center;

					@media (hover: hover) {
						&:hover,
						&--active {
							background-color: $color-neutral-500;
							color: $color-neutral-50;
						}
					}

					&:focus-visible {
						outline: $border-width-2 $border-style $color-focus;
					}
				}
			}
		}
	}
}

.title-max-width {
	@include max-w(260px);
}
.animate-bg {
	background: linear-gradient(-45deg, #f4f4f4, #f1f1f1, #d9d9d9);
	background-size: 400% 400%;
	animation: gradient-scroll 5s ease infinite;
}

@keyframes gradient-scroll {
	0% {
		background-position: 0% 50%;
	}
	50% {
		background-position: 100% 50%;
	}
	100% {
		background-position: 0% 50%;
	}
}
</style>
